import React, { lazy, Suspense } from "react";
import { RouteObject } from "react-router-dom";
import { AuthCardSkeleton } from "../components/ui/skeleton";

// Component Imports
import Dashboard from "../DashboardPage/components/dashboard/Dashboard";
import ViewSubmission from "../pages/view-submissions/ViewSubmission";
import FormPage from "../PitchFormPage/FormPage";
import EditFormPage from "../PitchFormPage/EditFormPage";
import InviteUserPage from "../pages/admin/inviteUser";
import LabelCodePage from "../pages/auth/LabelCodePage";
import ForgetPasswordPage from "../pages/auth/ForgetPasswordPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import UserDsps from "../pages/admin/UserDsps";
import PastSubmissions from "../pages/past-submissions/PastSubmissions";
import Labels from "../pages/labels/Labels";
import Settings from "../pages/settings/Settings";
import Analytics from "../pages/analytics/analytics";
import Transactions from "../pages/transactions/Transactions";
import Pricing from "../pages/pricing/Pricing";
import PrivacyPolicyPage from "../pages/privacy/PrivacyPolicyPage";
import Roster from "../pages/roster/Roster";
import AddArtist from "../pages/roster/add-artist/AddArtist";
import ArtistDetail from "../pages/roster/artist-details/ArtistDetail";
import StripeUpgrade from "../pages/subscription/StripeUpgrade";
import PaymentSuccess from "../pages/subscription/PaymentSuccess";
import PaymentCancel from "../pages/subscription/PaymentCancel";
import HandleSubscription from "../pages/subscription/HandleSubscription";
import CustomSubscription from "../pages/custom-subscription/CustomSubscription";
import ExclusivePermissions from "../pages/exclusive-permissions/ExclusivePermissions";
import AssignExclusivePermissions from "../pages/exclusive-permissions/AssignExclusivePermissions";
import AdminAnalytics from "../pages/admin/AdminAnalytics";
import MasterRoster from "../pages/admin/MasterRoster";
import AppUpdates from "../pages/app-updates/AppUpdates";
import DspOrganizations from "../pages/dsp-organizations/DspOrganizations";
import GmailCallback from "../pages/oauth-callback/gmailCallback";
import OutlookCallback from "../pages/oauth-callback/outlookCallback";
import Email from "../pages/email/Email";
import TemplateEditor from "../pages/email/TemplateEditor";
import EmailLogs from "../pages/email/EmailLogs";

import AnalyticsList from "../pages/analytics-list/analyticsList";
import ArtistAnalytics from "../pages/artist-analytics/artistAnalytics";

const SignUpPage = lazy(() => import("../pages/auth/SignUpPage"));
const LogInPage = lazy(() => import("../pages/auth/LogInPage"));

// Route configuration interface
export interface AppRoute {
  path: string;
  element: React.ReactNode;
}

export const publicRoutes: AppRoute[] = [
  {
    path: "/auth/verify-label",
    element: <LabelCodePage />,
  },
  {
    path: "/auth/signup",
    element: (
      <Suspense fallback={<AuthCardSkeleton />}>
        <SignUpPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/login",
    element: (
      <Suspense fallback={<AuthCardSkeleton />}>
        <LogInPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/forget-password",
    element: (
      <Suspense fallback={<AuthCardSkeleton />}>
        <ForgetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/reset-password",
    element: (
      <Suspense fallback={<AuthCardSkeleton />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: "/pricing",
    element: (
      <Suspense fallback={<AuthCardSkeleton />}>
        <Pricing />
      </Suspense>
    ),
  },
  {
    path: "/subscription/stripe-upgrade",
    element: (
      <Suspense fallback={<AuthCardSkeleton />}>
        <StripeUpgrade />
      </Suspense>
    ),
  },
  {
    path: "/success",
    element: (
      <Suspense fallback={<AuthCardSkeleton />}>
        <PaymentSuccess />
      </Suspense>
    ),
  },
  {
    path: "/cancel",
    element: (
      <Suspense fallback={<AuthCardSkeleton />}>
        <PaymentCancel />
      </Suspense>
    ),
  },
  {
    path: "/handle-subscription",
    element: (
      <Suspense fallback={<AuthCardSkeleton />}>
        <HandleSubscription />
      </Suspense>
    ),
  },
];

// Protected routes (require authentication)
export const privateRoutes: AppRoute[] = [
  {
    path: "/dashboard",
    element: <Dashboard />,
  },
  {
    path: "/analytics/detail/:id",
    element: <Analytics />,
  },
  {
    path: "/pitch/create",
    element: <FormPage />,
  },
  {
    path: "pitch/submission/:id",
    element: <ViewSubmission />,
  },
  {
    path: "pitch/edit/:id",
    element: <EditFormPage />,
  },
  {
    path: "/past-submissions",
    element: <PastSubmissions />,
  },
  {
    path: "/roster",
    element: <Roster />,
  },
  {
    path: "/roster/add-artist",
    element: <AddArtist />,
  },
  {
    path: "/roster/artist-details/:id",
    element: <ArtistDetail />,
  },
  {
    path: "/labels",
    element: <Labels />,
  },
  {
    path: "/settings",
    element: <Settings />,
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicyPage />,
  },
  {
    path: "/oauth/gmail/callback",
    element: <GmailCallback />,
  },
  {
    path: "/oauth/outlook/callback",
    element: <OutlookCallback />,
  },
  {
    path: "/email",
    element: <Email />,
  },
  {
    path: "/email/template/create",
    element: <TemplateEditor />,
  },
  {
    path: "/email/template/edit/:id",
    element: <TemplateEditor />,
  },
  {
    path: "/email/logs/:pitchId",
    element: <EmailLogs />,
  },{
    path: "/analytics",
    element: <AnalyticsList />
  },
   {
    path: "/artist/analytics-details/:id",
    element: <ArtistAnalytics />,
  },
];

export const adminRoutes: AppRoute[] = [
  {
    path: "/admin/invite",
    element: <InviteUserPage />,
  },
  {
    path: "/admin/userdsp/:id",
    element: <UserDsps />,
  },
  {
    path: "/admin/analytics",
    element: <AdminAnalytics />,
  },
  {
    path: "/master-roster",
    element: <MasterRoster />,
  },
  {
    path: "/transactions",
    element: <Transactions />,
  },
  {
    path: "/custom-subscription",
    element: <CustomSubscription />,
  },
  {
    path: "/exclusive-permissions",
    element: <ExclusivePermissions />,
  },
  {
    path: "/exclusive-permissions/create",
    element: <AssignExclusivePermissions />,
  },
  {
    path: "/app-updates",
    element: <AppUpdates />,
  },
  {
    path: "/dsp-organizations",
    element: <DspOrganizations />,
  },

];
